import { useState, useMemo, useEffect } from "react";
import {
  useDashboardMetrics,
  useMetricDetail,
  useToggleMetric,
} from "../../../common/infrastructure/api/hooks/useDashboardData";
import {
  METRIC_CONFIGS,
  canUserAccessMetric,
  isMetricRelevantForStudioTypes,
} from "../../../common/infrastructure/config/metricConfigs";
import { DashboardMetricKey } from "../../../common/enums";
import { useAuthentication } from "../../../common/infrastructure/hooks/useAuthentication";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { useAppContext } from "../../../AppContext";

export const useDashboard = () => {
  const { currentUserRole } = useAuthentication();
  const { selectedStudio } = useAppContext();

  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedMetricKey, setSelectedMetricKey] =
    useState<DashboardMetricKey | null>(null);
  const [togglingMetricKey, setTogglingMetricKey] =
    useState<DashboardMetricKey | null>(null);
  const [optimisticDisabledMetrics, setOptimisticDisabledMetrics] =
    useState<DashboardMetricKey[] | null>(null);

  // Format dates as YYYY-MM-DD in local time (not UTC) to avoid timezone issues
  const formatDateForApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const startDateStr = formatDateForApi(selectedStartDate);
  const endDateStr = formatDateForApi(selectedEndDate);

  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    error: metricsError,
  } = useDashboardMetrics({
    startDate: startDateStr,
    endDate: endDateStr,
  });

  const { data: detailData, isLoading: isDetailLoading } = useMetricDetail({
    metricKey: selectedMetricKey!,
    startDate: startDateStr,
    endDate: endDateStr,
    enabled: !!selectedMetricKey && isDetailModalOpen,
  });

  const { disableMetric, enableMetric } = useToggleMetric();

  // Clear optimistic state when server data updates (after successful refetch)
  useEffect(() => {
    if (metricsData?.disabledMetrics && optimisticDisabledMetrics !== null) {
      const serverData = metricsData.disabledMetrics;
      const optimisticData = optimisticDisabledMetrics;

      // If they match, clear optimistic state (server caught up)
      if (
        JSON.stringify([...serverData].sort()) ===
        JSON.stringify([...optimisticData].sort())
      ) {
        setOptimisticDisabledMetrics(null);
      }
    }
  }, [metricsData?.disabledMetrics, optimisticDisabledMetrics]);

  // Use optimistic disabled metrics if available, otherwise use server data
  const currentDisabledMetrics = useMemo(() => {
    return optimisticDisabledMetrics ?? metricsData?.disabledMetrics ?? [];
  }, [optimisticDisabledMetrics, metricsData?.disabledMetrics]);

  // Metrics relevant to the selected studio's types. Metrics without a
  // `studioTypes` restriction apply everywhere; the rest require the studio to
  // have at least one matching type. This fully hides irrelevant metrics — e.g.
  // a shop-only studio never sees reservation/class metrics, while every studio
  // with a shop component sees the sales metrics.
  const studioRelevantMetrics = useMemo(() => {
    const studioTypes = selectedStudio?.types ?? [];
    return METRIC_CONFIGS.filter((metric) =>
      isMetricRelevantForStudioTypes(metric, studioTypes)
    );
  }, [selectedStudio?.types]);

  // Metrics the current role may see (e.g. store turnover/profit are Manager+,
  // mirroring the backend, which returns null for them below that role). The
  // role is the numeric UserRole; an unknown role gets 0, so role-gated metrics
  // stay hidden (fail closed).
  const roleAllowedMetrics = useMemo(() => {
    const userRole = currentUserRole ?? 0;
    return studioRelevantMetrics.filter((metric) =>
      canUserAccessMetric(metric, userRole)
    );
  }, [studioRelevantMetrics, currentUserRole]);

  const visibleMetrics = useMemo(() => {
    return roleAllowedMetrics.filter(
      (metric) => !currentDisabledMetrics.includes(metric.key)
    );
  }, [roleAllowedMetrics, currentDisabledMetrics]);

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsCalendarOpen(false);
  };

  const handleMetricCardClick = (metricKey: DashboardMetricKey) => {
    const metric = METRIC_CONFIGS.find((m) => m.key === metricKey);
    if (metric?.hasDetail) {
      setSelectedMetricKey(metricKey);
      setIsDetailModalOpen(true);
    }
  };

  const handleCustomizationToggle = (metricKey: DashboardMetricKey) => {
    const isCurrentlyDisabled = currentDisabledMetrics.includes(metricKey);

    // Optimistic update
    const newDisabledMetrics = isCurrentlyDisabled
      ? currentDisabledMetrics.filter((key) => key !== metricKey)
      : [...currentDisabledMetrics, metricKey];

    setOptimisticDisabledMetrics(newDisabledMetrics);
    setTogglingMetricKey(metricKey);

    if (isCurrentlyDisabled) {
      enableMetric.mutate(
        { metricKey },
        {
          onError: () => {
            // Rollback optimistic update on error
            setOptimisticDisabledMetrics(null);
          },
          onSettled: () => {
            setTogglingMetricKey(null);
          },
        }
      );
    } else {
      disableMetric.mutate(
        { metricKey },
        {
          onError: () => {
            // Rollback optimistic update on error
            setOptimisticDisabledMetrics(null);
          },
          onSettled: () => {
            setTogglingMetricKey(null);
          },
        }
      );
    }
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedMetricKey(null);
  };

  const formatDateRange = () => {
    if (selectedStartDate.toDateString() === selectedEndDate.toDateString()) {
      return dateUtility.formatDate(selectedStartDate);
    }
    return `${dateUtility.formatDate(
      selectedStartDate
    )} - ${dateUtility.formatDate(selectedEndDate)}`;
  };

  const selectedMetricConfig = selectedMetricKey
    ? METRIC_CONFIGS.find((m) => m.key === selectedMetricKey)
    : null;

  return {
    // State
    selectedStartDate,
    selectedEndDate,
    isCalendarOpen,
    isCustomizationOpen,
    isDetailModalOpen,
    selectedMetricKey,
    togglingMetricKey,

    // Data
    metricsData,
    isMetricsLoading,
    metricsError,
    detailData,
    isDetailLoading,
    currentDisabledMetrics,
    visibleMetrics,
    customizationMetrics: roleAllowedMetrics,
    selectedMetricConfig,

    // Handlers
    handleDateRangeChange,
    handleMetricCardClick,
    handleCustomizationToggle,
    handleDetailModalClose,
    formatDateRange,
    setIsCalendarOpen,
    setIsCustomizationOpen,
  };
};
